import { useParams, Link } from "react-router-dom";
import { blogs } from "../data/blog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { motion } from "framer-motion";
import { useEffect } from "react";
import ShareButtons from "../components/ShareButtons";

export default function BlogPost() {
  const { slug } = useParams();
  const blog = blogs.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (!blog)
    return (
      <p className="text-center mt-20 text-slate-400 text-lg">
        Blog not found.
      </p>
    );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-16 text-slate-200">
      {/* --- Back Link --- */}
      <Link
        to="/blog"
        className="inline-block text-brand-blue hover:text-brand-teal transition-colors mb-6 sm:mb-8 text-sm font-semibold"
      >
        ← Back to Blog
      </Link>

      {/* --- Header --- */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 sm:mb-10"
      >
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-white mb-2 sm:mb-3 leading-tight">
          {blog.title}
        </h1>
        <p className="text-slate-400 text-sm sm:text-base">{blog.date}</p>
        {blog.banner && (
          <img
            src={blog.banner}
            alt={blog.title}
            className="rounded-2xl shadow-glow border border-white/10 my-6 sm:my-8 w-full max-h-[420px] object-cover"
          />
        )}
      </motion.div>

      {/* --- Blog Body --- */}
      <motion.article
        className="prose prose-invert max-w-none
                   prose-headings:mt-8 prose-headings:mb-3 sm:prose-headings:mt-10 sm:prose-headings:mb-4
                   prose-p:my-4 prose-p:leading-relaxed
                   prose-img:my-6 prose-img:rounded-2xl prose-img:shadow-glow prose-img:w-full prose-img:max-w-[90%] prose-img:mx-auto
                   prose-li:my-2 prose-hr:my-10 prose-strong:text-white
                   prose-code:text-brand-teal prose-code:bg-white/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                   text-[15px] sm:text-base"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {blog.content}
        </ReactMarkdown>
      </motion.article>

      {/* --- Footer Divider --- */}
      {/* --- Closing Message --- */}
      <div className="mt-16 text-center text-slate-300 text-sm leading-relaxed">
        Thanks for reading ☕️
        <br />
        If this brew resonated with you, I’d love your thoughts or feedback.
        <br /><br />
        Follow <a href="https://www.neuralbrew.net" className="text-brand-teal font-medium hover:underline">
          NeuralBrew
        </a> for more deep dives into real-world data systems.
      </div>
      {/* === Disclaimer Section === */}
      <div className="mt-8 text-center text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed">
        These thoughts are based on my experience and ongoing learning.
        I may not always be 100% correct, and I genuinely welcome feedback or alternative perspectives.
        <br /><br />
        The views expressed here are my own and do not represent the opinions of any
        current or former employer. This content is for educational purposes only
        and does not constitute professional advice.
      </div>

      {/* --- Share Button --- */}
      <div className="mt-6">
        <ShareButtons title={blog.title} />
      </div>

      {/* --- Divider & Signature --- */}
      <div className="mt-10 border-t border-white/10 pt-6 text-center text-sm text-slate-500">
        Written by{" "}
        <span className="text-brand-teal font-medium">Devish Mundra</span>
      </div>
    </div>
  );
}
